import { IWorkerCreate } from '../../interfaces/worker.interface';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  IWorkerDocumentUpdateData,
  IWorkerList,
  IWorkerOnboardingDetails,
  IWorkerPreferenceData,
  IWorkerSkillAndExperienceData,
  WorkerCounts,
  IMapWorkerQualificationData,
  IWorkerViewStatusUpdateState,
  IRateWorkerRequest,
  IRateWorkerResponse,
  IUpdateWorkerRatingRequest,
  IUpdateWorkerRatingResponse,
  IUpdateWorkerRatesRequest,
  IUpdateWorkerRatesResponse,
  IWorkerPreferredTimingsResponse,
  IWorkerStatistics,
  IWorkerShiftHistoryResponse,
  IWorkerCustomForm,
  IWorkerRoleDocumentRequirement,
  IWorkerBulkUploadResponse,
  IWorkerBulkUploadDetail,
  IWorkerBulkUploadSummary,
} from './types';


import apiCall from 'utils/api';

export const getWorkerListRequest = createAction('worker/getListRequest');
export const getWorkerListSuccess = createAction<IWorkerList>('worker/getListSuccess');
export const getWorkerListFailure = createAction<string>('worker/getListFailure');

export const updateWorkerSkillAndExperience = createAsyncThunk(
  'worker/updateWorkerSkillAndExperience',
  async (workerData: IWorkerSkillAndExperienceData, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ message: string }>('post', '/api/worker/onboard/qualification-experience/', workerData);
      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to update worker skill and experience');
    }
  }
)

export const updateWorkerDocuments = createAsyncThunk(
  'worker/updateWorkerDocuments',
  async (workerDcoumentsData: IWorkerDocumentUpdateData, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ message: string }>('post', '/api/worker/onboarding/documents/bulk-update/', workerDcoumentsData);
      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to update worker documents');
    }
  }
)

export const updateWorkerPrefrence = createAsyncThunk(
  'worker/updateWorkerPrefrence',
  async (workerData: IWorkerPreferenceData, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ message: string }>('post', '/api/worker/onboard/preferences/', workerData);
      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to update worker preference');
    }
  }
)

export const fetchWorkerCounts = createAsyncThunk(
  'worker/fetchWorkerCounts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: WorkerCounts }>('get', '/worker/counts/');
      return response.data.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch worker counts');
    }
  }
);

export const resentWokreInvitation = createAsyncThunk(
  'worker/resentWokreInvitation',
  async (workerId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ message: string }>('put', `/api/worker/resend-invitation/${workerId}/`);
      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to resend worker invitation');
    }
  }
)

export const createWorker = createAsyncThunk(
  'worker/createWorker',
  async (workerData: IWorkerCreate, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ message: string }>('post', '/worker/onboard/', workerData);
      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to create worker');
    }
  }
)

export const fetchPendingWorkerrList = createAsyncThunk(
  'worker/fetchPendingWorkerrList',
  async ({ page, pageSize, search }: { page: number; pageSize: number; search?: string }, { rejectWithValue }) => {
    try {
      let url = `/worker/list/?page=${page}&page_size=${pageSize}&status=pending`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      const response = await apiCall<{ data: IWorkerList }>('get', url);
      return response.data.data; // Access the nested 'data' property
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch worker list');
    }
  }
)

export const fetchWorkerDetail = createAsyncThunk(
  'worker/fetchWorkerDetail',
  async (workerId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IWorkerOnboardingDetails[] }>('get', `/api/worker/onboarding/details/${workerId}/`);
      return response.data.data[0]; // Access the first element of the nested 'data' property
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch worker detail');
    }
  }
)

export const fetchWorkerList = createAsyncThunk(
  'worker/fetchWorkerList',
  async (
    {
      page,
      pageSize,
      qualificationId,
      locationName,
      yearsOfExperienceMin,
      yearsOfExperienceMax,
      facilityId,
      searchTerm,
      status,
      orderBy,
      order,
      roleId
    }:
      {
        page: number;
        pageSize: number;
        qualificationId?: number;
        locationName?: string;
        yearsOfExperienceMin?: number;
        yearsOfExperienceMax?: number;
        facilityId?: number | null;
        searchTerm?: string | null;
        status: string;
        orderBy?: string;
        order?: string;
        roleId?: number;
      },
    { rejectWithValue }
  ) => {
    try {
      let url = `/worker/list/?page=${page}&page_size=${pageSize}`;
      if (qualificationId) {
        url += `&qualification=${qualificationId}`;
      }
      if (locationName) {
        url += `&location=${encodeURIComponent(locationName)}`;
      }
      if (yearsOfExperienceMin !== undefined) {
        url += `&years_of_experience_min=${yearsOfExperienceMin}`;
      }
      if (yearsOfExperienceMax !== undefined) {
        url += `&years_of_experience_max=${yearsOfExperienceMax}`;
      }
      if (facilityId !== null && facilityId !== undefined) {
        url += `&facility=${facilityId}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      if (roleId) {
        url += `&role=${roleId}`;
      }
      if (status && status !== 'All') {
        url += `&status=${encodeURIComponent(status)}`;
      }
      if (orderBy) {
        url += `&orderby=${orderBy}`;
      }
      if (order) {
        url += `&order=${order}`;
      }
      const response = await apiCall<{ data: IWorkerList }>('get', url);
      return response.data.data; // Access the nested 'data' property
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch worker list');
    }
  }
);

export const fetchPastShiftWorkerList = createAsyncThunk(
  'worker/fetchPastShiftWorkerList',
  async (
    {
      page,
      pageSize,
      role,
      startTime,
      endTime,
      excludeIds,
      searchTerm
    }: {
      page: number;
      pageSize: number;
      role?: number;
      startTime?: string;
      endTime?: string;
      excludeIds?: string;
      searchTerm?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      let url = `/worker/past-shift/list/?page=${page}&page_size=${pageSize}`;
      if (role) url += `&role=${role}`;
      if (startTime) url += `&start_time=${startTime}`;
      if (endTime) url += `&end_time=${endTime}`;
      if (excludeIds) url += `&exclude_ids=${excludeIds}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;

      const response = await apiCall<{ data: IWorkerList }>('get', url);
      return response.data.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch past shift worker list');
    }
  }
);

export const deleteWorkerQualification = createAsyncThunk(
  'worker/deleteWorkerQualification',
  async (qualificationId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall('delete', `/worker/qualification/delete/${qualificationId}/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete worker qualification');
    }
  }
);

export const updateWorkerQualification = createAsyncThunk(
  'worker/updateWorkerQualification',
  async ({ workerQualificationId, qualificationId }: { workerQualificationId: number, qualificationId: number }, { rejectWithValue }) => {
    try {
      const response = await apiCall('put', `/worker/qualification/update/${workerQualificationId}/`, { qualification: qualificationId });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update worker qualification');
    }
  }
);

export const updateWorkerStatus = createAsyncThunk(
  'worker/updateWorkerStatus',
  async ({ workerId, action, reason }: { workerId: number; action: string; reason?: string }, { rejectWithValue }) => {
    try {
      const payload: any = { action };
      if (reason) payload.reason = reason;
      const response = await apiCall<{ message: string }>('post', `/api/worker/status/${workerId}/`, payload);
      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to update worker status');
    }
  }
);

export const mapWorkerQualification = createAsyncThunk(
  'worker/mapWorkerQualification',
  async (qualificationData: IMapWorkerQualificationData, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ message: string }>('post', '/api/worker/qualification/map/organization/', qualificationData);
      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to map worker qualification');
    }
  }
);

export const updateWorkerDocumentStatus = createAsyncThunk(
  'worker/updateWorkerDocumentStatus',
  async (
    {
      documentId,
      action,
      remarks,
      document_issued_on,
      document_expiring_on
    }: {
      documentId: number;
      action: 'accept' | 'reject';
      remarks?: string;
      document_issued_on?: string;
      document_expiring_on?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall<{ message: string }>(
        'post',
        `/api/worker/document/status/${documentId}/`,
        {
          action,
          remarks,
          document_issued_on,
          document_expiring_on
        }
      );
      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to update worker document status');
    }
  }
);

export const markWorkerAsViewed = createAsyncThunk(
  'worker/markWorkerAsViewed',
  async (workerId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IWorkerViewStatusUpdateState; status: boolean }>('patch', `/api/worker/viewed/${workerId}/`);
      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to update worker view status');
    }
  }
);

export const updateWorkerViolationFlag = createAsyncThunk(
  'worker/updateWorkerViolationFlag',
  async ({ workerId, violation_flag }: { workerId: number; violation_flag: boolean }, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: any }>('put', `/api/worker/flag/${workerId}/`, { violation_flag });
      return response.data;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      return rejectWithValue(error.response?.data?.message || 'Failed to update worker violation flag');
    }
  }
);

export const rateWorker = createAsyncThunk(
  'worker/rateWorker',
  async (
    { workerId, data }: { workerId: number; data: IRateWorkerRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<IRateWorkerResponse>(
        'post',
        `/worker/ratings/${workerId}/`,
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to rate worker');
    }
  },
);

export const updateWorkerRating = createAsyncThunk(
  'worker/updateWorkerRating',
  async (
    { ratingId, data }: { ratingId: number; data: IUpdateWorkerRatingRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<IUpdateWorkerRatingResponse>(
        'patch',
        `/worker/ratings/update/${ratingId}/`,
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update worker rating');
    }
  },
);

export const updateWorkerRates = createAsyncThunk(
  'worker/updateWorkerRates',
  async (
    { workerId, data }: { workerId: number; data: IUpdateWorkerRatesRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiCall<IUpdateWorkerRatesResponse>(
        'put',
        `/worker/update-rates/${workerId}/`,
        data,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update worker rates');
    }
  },
);

export const fetchWorkerPreferredTimings = createAsyncThunk(
  'worker/fetchWorkerPreferredTimings',
  async ({ workerId, page = 1, pageSize = 10 }: { workerId: number, page?: number, pageSize?: number }, { rejectWithValue }) => {
    try {
      const response = await apiCall<IWorkerPreferredTimingsResponse>(
        'get',
        `/api/worker/preferred-timing/${workerId}/?page=${page}&page_size=${pageSize}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch preferred timings');
    }
  }
);


export const fetchWorkerStatistics = createAsyncThunk(
  'worker/fetchWorkerStatistics',
  async (workerId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IWorkerStatistics }>('get', `/api/organization/statistics/worker/${workerId}/`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch worker statistics');
    }
  }
);


export const fetchWorkerShiftHistory = createAsyncThunk(
  'worker/fetchWorkerShiftHistory',
  async (
    {
      workerId,
      page = 1,
      pageSize = 10,
      ordering = '-shift_start_time'
    }: {
      workerId: number;
      page?: number;
      pageSize?: number;
      ordering?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall<{ data: IWorkerShiftHistoryResponse }>(
        'get',
        `/api/organization/shifts/worker-history/?worker_id=${workerId}&page=${page}&page_size=${pageSize}&ordering=${ordering}`
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch worker shift history');
    }
  }
);

export const fetchWorkerCustomFormSubmissions = createAsyncThunk(
  'worker/fetchWorkerCustomFormSubmissions',
  async (workerId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IWorkerCustomForm[]; status: boolean }>(
        'get',
        `/api/worker/custom-form/submissions/${workerId}/`
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch worker custom form submissions');
    }
  }
);

export const fetchWorkerRoleDocumentRequirementsBulk = createAsyncThunk(
  'worker/fetchWorkerRoleDocumentRequirementsBulk',
  async (workerRoleIds: number[], { rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: IWorkerRoleDocumentRequirement[] } | IWorkerRoleDocumentRequirement[]>('post', '/api/worker/worker-role-document-requirements/', { worker_role_ids: workerRoleIds });
      // Flexibly handle if data is nested or direct list
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return (response.data as { data: IWorkerRoleDocumentRequirement[] }).data || [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch worker role document requirements');
    }
  }
);
export const bulkUploadWorkers = createAsyncThunk(
  'worker/bulkUploadWorkers',
  async (file: File, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiCall<{ message: string }>('post', '/api/worker/bulk-upload/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to upload workers');
    }
  }
);

export const setBulkUploadData = createAction<IWorkerBulkUploadResponse>('worker/setBulkUploadData');
export const updateBulkUploadProgress = createAction<{ detail: IWorkerBulkUploadDetail, summary: IWorkerBulkUploadSummary }>('worker/updateBulkUploadProgress');
export const resetBulkUploadState = createAction('worker/resetBulkUploadState');
